import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { navApi, marketApi, plansApi } from "../lib/api";
import { Send, Sparkles, Mic, Pause, FileCheck, AlertTriangle, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { CODLogoSVG } from "../components/cod/Logo";

const NavigatorAvatar = ({ size = 36 }) => (
  <div className="rounded-full flex items-center justify-center bg-white border-2 border-[var(--cod-navy)] flex-shrink-0"
    style={{ width: size, height: size }} aria-hidden>
    <CODLogoSVG size={Math.round(size * 0.72)}/>
  </div>
);

export default function Navigator() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [readyForPlan, setReadyForPlan] = useState(false);
  const [priority, setPriority] = useState("P3");
  const scrollRef = useRef(null);
  const [addedSlugs, setAddedSlugs] = useState({}); // slug -> true after add
  const [addingSlug, setAddingSlug] = useState(null);

  const opportunitySlug = params.get("opportunity");
  const [opportunityName, setOpportunityName] = useState("");

  useEffect(() => {
    if (opportunitySlug) {
      // Resolve slug to friendly name for greeting
      marketApi.opportunity(opportunitySlug).then(o => setOpportunityName(o?.name || "")).catch(()=>{});
    }
  }, [opportunitySlug]);

  useEffect(() => {
    const opener = opportunitySlug && opportunityName
      ? (user
          ? `Hi ${user.full_name.split(" ")[0]} — I see you're interested in ${opportunityName}. Want me to walk you through what it takes to start?`
          : `Hi — I see you're interested in ${opportunityName}. Want me to walk you through what it takes to start?`)
      : (user
          ? `Hi ${user.full_name.split(" ")[0]} — I'm the COD Opportunity Navigator. What brings you here today?`
          : "Hi — I'm the COD Opportunity Navigator. What brings you here today?");
    setMessages([{ role: "assistant", text: opener, ts: new Date().toISOString() }]);
  }, [user, opportunitySlug, opportunityName]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", text, ts: new Date().toISOString() }]);
    setBusy(true);
    try {
      const res = await navApi.chat({
        conversation_id: convId,
        message: text,
        opportunity_slug: opportunitySlug || undefined,
      });
      setConvId(res.conversation_id);
      setMessages(m => [...m, { role: "assistant", text: res.reply, ts: new Date().toISOString(), escalation: res.escalation, recommendations: res.recommendations || [] }]);
      if (res.priority) setPriority(res.priority);
      if (res.ready_for_plan) setReadyForPlan(true);
      if (res.escalation) {
        toast.warning(`${res.escalation.priority} priority — a Case Manager has been notified.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach the Navigator.");
    } finally { setBusy(false); }
  };

  const buildPlan = async () => {
    if (!convId) return;
    setBusy(true);
    try {
      const plan = await navApi.generatePlan(convId);
      toast.success("Your Opportunity Plan is ready.");
      navigate(`/app/plan?new=${plan.id}`);
    } catch (e) {
      toast.error("Plan generation failed.");
    } finally { setBusy(false); }
  };

  const addToPlan = async (rec) => {
    if (!rec?.slug || addingSlug) return;
    setAddingSlug(rec.slug);
    try {
      const res = await plansApi.addItem(rec.slug);
      setAddedSlugs(m => ({ ...m, [rec.slug]: true }));
      if (res.added) {
        toast.success(`Added "${rec.name}" to your plan.`);
      } else {
        toast.info(`"${rec.name}" is already in your plan.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to your plan.");
    } finally { setAddingSlug(null); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="cod-eyebrow">Opportunity Navigator</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Talk with the Navigator</h1>
            <div className="cod-rule-red mt-3"/>
            <p className="text-sm text-slate-600 mt-3">One question at a time. Take your time.</p>
          </div>
          <div className="flex items-center gap-2">
            {priority !== "P3" && (
              <Badge variant={priority === "P1" ? "destructive" : "default"} data-testid="priority-badge">
                <AlertTriangle size={12} className="mr-1"/> {priority}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={()=>setPaused(!paused)} data-testid="pause-btn">
              <Pause size={14} className="mr-1"/> {paused ? "Resume" : "Pause"}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div ref={scrollRef} className="space-y-4 max-h-[55vh] overflow-y-auto scrollbar-thin pr-2" data-testid="conversation-transcript">
            {messages.map((m, i) => (
              <div key={m.ts || `msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <NavigatorAvatar size={36}/>
                )}
                <div className={m.role === "user" ? "cod-bubble-user max-w-[80%]" : "cod-bubble-ai max-w-[85%] ml-2"}
                  data-testid={`msg-${m.role}-${i}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                  {m.role === "assistant" && m.recommendations && m.recommendations.length > 0 && user?.role === "participant" && (
                    <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`recs-${i}`}>
                      <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">One-click actions</p>
                      <div className="flex flex-wrap gap-2">
                        {m.recommendations.map((rec) => {
                          const added = addedSlugs[rec.slug];
                          const loading = addingSlug === rec.slug;
                          return (
                            <button
                              key={rec.slug}
                              onClick={() => addToPlan(rec)}
                              disabled={added || loading}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${added ? "bg-emerald-50 border-emerald-300 text-emerald-800 cursor-default" : "bg-white border-slate-300 text-slate-800 hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)]"}`}
                              data-testid={`add-to-plan-${rec.slug}`}
                              aria-label={added ? `${rec.name} added to your plan` : `Add ${rec.name} to my plan`}
                            >
                              {added ? <Check size={12}/> : <Plus size={12}/>}
                              {added ? `Added: ${rec.name}` : `Add ${rec.name} to my plan`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {m.escalation && (
                    <div className="mt-2 text-xs text-red-700 border-t border-red-200 pt-2 flex items-start gap-1">
                      <AlertTriangle size={12} className="mt-0.5"/> A Case Manager will reach out soon. <span className="font-medium">({m.escalation.priority})</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="navigator-thinking">
                <NavigatorAvatar size={36}/>
                <Sparkles size={14}/> Thinking...
              </div>
            )}
          </div>

          {readyForPlan && convId && (
            <div className="mt-4 p-4 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center justify-between" data-testid="plan-ready-banner">
              <div className="flex items-center gap-2 text-emerald-900">
                <FileCheck size={18}/> <span className="text-sm">I have enough to put together your Opportunity Plan.</span>
              </div>
              <Button onClick={buildPlan} disabled={busy} data-testid="build-plan-btn" style={{ background: "var(--cod-red)" }}>
                Build my plan
              </Button>
            </div>
          )}

          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="flex gap-2 items-end">
              <Textarea value={input} onChange={e=>setInput(e.target.value)} placeholder="Type what's on your mind..."
                disabled={paused || busy} rows={2} className="resize-none"
                onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey){ e.preventDefault(); send();} }}
                data-testid="nav-input"/>
              <Button variant="outline" disabled className="hidden sm:inline-flex" data-testid="mic-btn"><Mic size={16}/></Button>
              <Button onClick={send} disabled={busy || !input.trim() || paused} className="cod-bg-red" data-testid="send-btn">
                <Send size={16}/>
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Voice mode coming soon · Press Enter to send</p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
